import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import DashboardHeader from '../../components/DashboardHeader';
import FlagIcon from '../../components/FlagIcon';
import { PlusIcon, UsersIcon, TrashIcon } from '../../assets/icons';
import ErrorToast from '../../components/ErrorToast';
import SuccessToast from '../../components/SuccessToast';
import HansePayLoader from '../../components/HansePayLoader';
import * as recipientsActions from '../../store/actions/recipients';
import * as transactionsActions from '../../store/actions/transactions';
import {
  buildLastSentMap,
  canSendToRecipient,
  formatAccountDisplay,
  formatRelativeLastSent,
  getBankLabel,
  getCountryInfo,
  getInitials,
  getLastSentDate,
  isBusinessRecipient,
} from '../../utils/recipientHelpers';

const CARD_CLASS = 'rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-sm)]';
const TABLE_CARD_CLASS = 'rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] shadow-[var(--shadow-md)] overflow-hidden';
const ROW_GRID = 'grid grid-cols-[minmax(0,1.6fr)_minmax(0,1.3fr)_110px_90px] gap-3.5';

function FilterPill({ active, onClick, children }) {
  return (
    <button
      type='button'
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-[12.5px] font-medium font-[family-name:var(--font-ui)] transition-colors ${
        active
          ? 'bg-[var(--white)] text-[var(--n800)] shadow-[0_1px_3px_rgba(11,25,41,0.1)]'
          : 'bg-transparent text-[var(--text-tertiary)]'
      }`}
    >
      {children}
    </button>
  );
}

FilterPill.propTypes = {
  active: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

function DeleteConfirmationModal({ isOpen, onClose, onConfirm, recipientName, isDeleting }) {
  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center'>
      <div
        className='fixed inset-0 bg-black/50'
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role='button'
        tabIndex={0}
        aria-label='Close modal'
      />
      <div className='relative z-10 mx-4 w-full max-w-md rounded-[14px] border border-[var(--color-border)] bg-[var(--white)] p-6 shadow-[var(--shadow-lg)]'>
        <h3 className='mb-2 text-lg font-medium text-[var(--n900)]'>Delete recipient</h3>
        <p className='mb-6 text-sm text-[var(--text-secondary)]'>
          Are you sure you want to delete <span className='font-medium text-[var(--n800)]'>{recipientName}</span>? This action cannot be undone.
        </p>
        <div className='flex justify-end gap-3'>
          <button
            type='button'
            onClick={onClose}
            className='inline-flex h-10 items-center justify-center rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-4 text-sm font-medium text-[var(--n800)] hover:bg-[var(--off)]'
          >
            Cancel
          </button>
          <button
            type='button'
            onClick={onConfirm}
            disabled={isDeleting}
            className='inline-flex h-10 items-center justify-center rounded-lg bg-[var(--color-error)] px-4 text-sm font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50'
          >
            {isDeleting ? 'Deleting…' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

DeleteConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  recipientName: PropTypes.string.isRequired,
  isDeleting: PropTypes.bool.isRequired,
};

function RecipientRow({ recipient, countryInfo, lastSentLabel, canSend, onSend, onDelete, onOpen, isHighlighted }) {
  const isBusiness = isBusinessRecipient(recipient);
  const initials = getInitials(recipient.name);
  const accountLine = formatAccountDisplay(recipient);
  const bankLine = getBankLabel(recipient);

  return (
    <div
      role='button'
      tabIndex={0}
      onClick={() => onOpen(recipient.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen(recipient.id);
        }
      }}
      className={`${ROW_GRID} cursor-pointer items-center border-b border-[var(--color-border)] px-5 py-3.5 transition-colors last:border-b-0 ${
        isHighlighted
          ? 'bg-[#e8f2fa] ring-1 ring-inset ring-[#b8d4eb]'
          : 'hover:bg-[var(--off)]'
      }`}
    >
      <div className='flex min-w-0 items-center gap-3'>
        <div
          className={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-[10px] text-xs font-semibold font-[family-name:var(--font-ui)] ${
            isBusiness
              ? 'bg-[var(--n800)] text-white'
              : 'border border-[var(--color-border)] bg-[var(--off)] text-[var(--n700)]'
          }`}
        >
          {initials}
        </div>
        <div className='min-w-0 flex-1'>
          <div className='flex min-w-0 items-center gap-1.5'>
            <span className='truncate text-sm font-medium text-[var(--n900)]'>{recipient.name || '—'}</span>
            {recipient.currency && (
              <span
                className='flex h-5 w-5 shrink-0 items-center justify-center rounded-[8px] border border-[var(--color-border)] bg-[var(--off)]'
                aria-hidden='true'
              >
                <FlagIcon currencyCode={recipient.currency} className='h-2.5 w-3 rounded-sm' />
              </span>
            )}
          </div>
          <div className='mt-0.5 flex min-w-0 items-center gap-1.5 truncate text-xs text-[var(--text-tertiary)]'>
            <span className='capitalize'>{isBusiness ? 'business' : 'individual'}</span>
            <span>·</span>
            <span className='truncate'>{countryInfo.name}</span>
          </div>
        </div>
      </div>

      <div className='min-w-0'>
        <div className='truncate text-[12.5px] tabular-nums text-[var(--n800)] font-[family-name:var(--font-ui)]'>{accountLine}</div>
        <div className='mt-0.5 truncate text-[11.5px] text-[var(--text-tertiary)]'>
          {bankLine}
          {recipient.currency && (
            <>
              {' '}
              · <span className='font-semibold tracking-[0.04em]'>{recipient.currency}</span>
            </>
          )}
        </div>
      </div>

      <div className='text-[12.5px] text-[var(--n800)]'>{lastSentLabel}</div>

      <div className='flex items-center justify-end gap-1'>
        <button
          type='button'
          onClick={(e) => {
            e.stopPropagation();
            if (canSend) onSend(recipient.id);
          }}
          disabled={!canSend}
          className={`rounded-[7px] border px-3 py-1.5 text-xs font-medium font-[family-name:var(--font-ui)] ${
            canSend
              ? 'border-[var(--n800)] bg-transparent text-[var(--n800)] hover:bg-[var(--off)]'
              : 'cursor-not-allowed border-[var(--color-border)] text-[var(--text-tertiary)]'
          }`}
        >
          Send
        </button>
        <button
          type='button'
          aria-label={`Delete ${recipient.name || 'recipient'}`}
          onClick={(e) => {
            e.stopPropagation();
            onDelete(recipient);
          }}
          className='inline-flex h-8 w-8 items-center justify-center rounded-[7px] text-[var(--text-tertiary)] transition-colors hover:bg-[var(--off)] hover:text-[var(--color-error)]'
        >
          <TrashIcon className='h-4 w-4' />
        </button>
      </div>
    </div>
  );
}

RecipientRow.propTypes = {
  recipient: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    currency: PropTypes.string,
    accountId: PropTypes.string,
    iban: PropTypes.string,
    accountNumber: PropTypes.string,
    bankAccountNumber: PropTypes.string,
    allowFirstTransfer: PropTypes.bool,
    isValid: PropTypes.bool,
    status: PropTypes.string,
  }).isRequired,
  countryInfo: PropTypes.shape({
    code: PropTypes.string,
    name: PropTypes.string,
    flag: PropTypes.string,
  }).isRequired,
  lastSentLabel: PropTypes.string.isRequired,
  canSend: PropTypes.bool.isRequired,
  onSend: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onOpen: PropTypes.func.isRequired,
  isHighlighted: PropTypes.bool,
};

RecipientRow.defaultProps = {
  isHighlighted: false,
};

function Recipients() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, recipientId: null, offRampProvider: null, recipientName: '' });
  const [waitingForDelete, setWaitingForDelete] = useState(false);
  const [showSuccessToast, setShowSuccessToast] = useState(false);
  const itemsPerPage = 10;

  const { recipients, gettingRecipients, getRecipientsError, deletingRecipient, deleteRecipientError, highlightedRecipientId } =
    useSelector((state) => state.recipients);
  const { transactions } = useSelector((state) => state.transactions);
  const { currencies: configCurrencies } = useSelector((state) => state.config);

  useEffect(() => {
    if (recipients == null && !gettingRecipients && !getRecipientsError) {
      dispatch(recipientsActions.getRecipients());
    }
    dispatch(transactionsActions.getTransactions());
  }, [dispatch, recipients, gettingRecipients, getRecipientsError]);

  useEffect(() => {
    if (!highlightedRecipientId) return undefined;
    setCurrentPage(1);
    const timer = setTimeout(() => {
      dispatch(recipientsActions.clearHighlightedRecipient());
    }, 5 * 60 * 1000);
    return () => clearTimeout(timer);
  }, [dispatch, highlightedRecipientId]);

  useEffect(() => {
    if (waitingForDelete && deletingRecipient === false) {
      if (!deleteRecipientError) {
        setShowSuccessToast(true);
        setTimeout(() => setShowSuccessToast(false), 3000);
      }
      setWaitingForDelete(false);
      setDeleteModal({ isOpen: false, recipientId: null, offRampProvider: null, recipientName: '' });
    }
  }, [waitingForDelete, deletingRecipient, deleteRecipientError]);

  useEffect(() => {
    setCurrentPage(1);
  }, [typeFilter, searchQuery]);

  const lastSentMap = useMemo(() => buildLastSentMap(transactions), [transactions]);

  const allRecipients = useMemo(() => (recipients || []).filter((recipient) => recipient && recipient.id), [recipients]);

  const filteredRecipients = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    return allRecipients.filter((recipient) => {
      const isBusiness = isBusinessRecipient(recipient);
      if (typeFilter === 'business' && !isBusiness) return false;
      if (typeFilter === 'individual' && isBusiness) return false;

      if (!query) return true;

      const country = getCountryInfo(recipient, configCurrencies);
      const account = formatAccountDisplay(recipient).toLowerCase();
      return (
        String(recipient.name || '')
          .toLowerCase()
          .includes(query) ||
        country.name.toLowerCase().includes(query) ||
        String(country.code || '')
          .toLowerCase()
          .includes(query) ||
        account.includes(query.replace(/\s/g, '')) ||
        String(recipient.currency || '')
          .toLowerCase()
          .includes(query) ||
        String(recipient.accountId || '')
          .toLowerCase()
          .includes(query)
      );
    });
  }, [allRecipients, typeFilter, searchQuery, configCurrencies]);

  const stats = useMemo(() => {
    const businessCount = allRecipients.filter((r) => isBusinessRecipient(r)).length;
    const individualCount = allRecipients.length - businessCount;
    const countries = new Set(
      allRecipients.map((r) => getCountryInfo(r, configCurrencies).code || getCountryInfo(r, configCurrencies).name).filter(Boolean),
    );
    return {
      total: allRecipients.length,
      business: businessCount,
      individual: individualCount,
      countries: countries.size,
    };
  }, [allRecipients, configCurrencies]);

  const totalItems = filteredRecipients.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedRecipients = filteredRecipients.slice(startIndex, startIndex + itemsPerPage);

  const pageNumbers = useMemo(() => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
    } else if (currentPage <= 3) {
      pages.push(1, 2, 3, '…', totalPages);
    } else if (currentPage >= totalPages - 2) {
      pages.push(1, '…', totalPages - 2, totalPages - 1, totalPages);
    } else {
      pages.push(1, '…', currentPage, '…', totalPages);
    }
    return pages;
  }, [currentPage, totalPages]);

  const startItem = totalItems === 0 ? 0 : startIndex + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  const handleAddRecipient = () => navigate('/select-currency');

  const handleDeleteClick = (recipient) => {
    setDeleteModal({
      isOpen: true,
      recipientId: recipient.id,
      offRampProvider: recipient.offRampProvider,
      recipientName: recipient.name || 'this recipient',
    });
  };

  const handleDeleteConfirm = () => {
    setWaitingForDelete(true);
    dispatch(
      recipientsActions.deleteRecipient({
        offRampProvider: deleteModal.offRampProvider,
        recipientId: deleteModal.recipientId,
      }),
    );
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, recipientId: null, offRampProvider: null, recipientName: '' });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <Layout hideHeader mainPaddingClassName='px-6 py-9 sm:px-11'>
      <div className='flex flex-col gap-7'>
        <DashboardHeader navLabel='Recipients' title='Your recipients' />

        <div className='flex flex-col gap-6'>
          <div className='flex flex-wrap items-end justify-between gap-6'>
            <p className='m-0 max-w-[540px] text-sm text-[var(--text-secondary)]'>
              Trusted businesses and individuals you&apos;ve sent money to. Pick one to start a transfer in seconds.
            </p>
            <button
              type='button'
              onClick={handleAddRecipient}
              className='inline-flex items-center gap-2 rounded-lg bg-[var(--n800)] px-[22px] py-3 text-sm font-medium text-white shadow-[var(--shadow-cta)] transition-opacity hover:opacity-90 font-[family-name:var(--font-ui)]'
            >
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                <path d='M12 5v14M5 12h14' />
              </svg>
              Add recipient
            </button>
          </div>

          <div className={`${CARD_CLASS} grid grid-cols-2 overflow-hidden lg:grid-cols-4`}>
            {[
              { label: 'Total recipients', value: stats.total },
              { label: 'Businesses', value: stats.business },
              { label: 'Individuals', value: stats.individual },
              { label: 'Countries', value: stats.countries },
            ].map((stat, index) => (
              <div
                key={stat.label}
                className={`px-6 py-5 ${index < 3 ? 'border-b border-[var(--color-border)] lg:border-b-0 lg:border-r' : 'border-b-0'}`}
              >
                <div className='mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-[var(--text-tertiary)]'>{stat.label}</div>
                <div className='font-display text-[30px] font-normal leading-none tracking-[-0.02em] text-[var(--n900)]'>{stat.value}</div>
              </div>
            ))}
          </div>

          <div className='flex flex-wrap items-center gap-3'>
            <div className='flex gap-0.5 rounded-lg border border-[var(--color-border)] bg-[var(--off)] p-[3px]'>
              <FilterPill active={typeFilter === 'all'} onClick={() => setTypeFilter('all')}>
                All
              </FilterPill>
              <FilterPill active={typeFilter === 'business'} onClick={() => setTypeFilter('business')}>
                Businesses
              </FilterPill>
              <FilterPill active={typeFilter === 'individual'} onClick={() => setTypeFilter('individual')}>
                Individuals
              </FilterPill>
            </div>

            <div className='relative min-w-[220px] flex-1'>
              <svg width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='var(--text-tertiary)' strokeWidth='1.8' className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2' aria-hidden='true'>
                <circle cx='11' cy='11' r='8' />
                <path d='M21 21l-4.35-4.35' />
              </svg>
              <input
                type='search'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder='Search by name, country, or IBAN…'
                className='w-full rounded-lg border border-[var(--color-border)] bg-[var(--white)] py-2 pl-[34px] pr-3.5 text-[13px] text-[var(--n900)] outline-none placeholder:text-[var(--text-tertiary)] focus:ring-2 focus:ring-[var(--n200)]'
              />
            </div>
          </div>

          {gettingRecipients && <HansePayLoader variant='inline' />}

          {!gettingRecipients && Array.isArray(recipients) && recipients.length === 0 && (
            <div className={`${CARD_CLASS} flex flex-col items-center px-6 py-14 text-center`}>
              <UsersIcon className='mb-4 h-14 w-14 text-[var(--text-tertiary)] opacity-50' />
              <h3 className='mb-2 text-lg font-medium text-[var(--n800)]'>No recipients yet</h3>
              <p className='mb-6 max-w-md text-sm text-[var(--text-secondary)]'>
                Start by adding your first recipient to make money transfers faster and easier.
              </p>
              <button
                type='button'
                onClick={handleAddRecipient}
                className='inline-flex items-center gap-2 rounded-lg bg-[var(--n800)] px-5 py-2.5 text-sm font-medium text-white shadow-[var(--shadow-cta)] hover:opacity-90'
              >
                <PlusIcon className='h-4 w-4' />
                Add recipient
              </button>
            </div>
          )}

          {!gettingRecipients && allRecipients.length > 0 && totalItems === 0 && (
            <div className={`${CARD_CLASS} px-6 py-12 text-center text-sm text-[var(--text-secondary)]`}>
              No recipients match your current filters.
            </div>
          )}

          {!gettingRecipients && totalItems > 0 && (
            <>
              <div className={TABLE_CARD_CLASS}>
                <div className={`${ROW_GRID} border-b border-[var(--color-border)] bg-[var(--off)] px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.12em] text-[var(--text-tertiary)]`}>
                  <div>Recipient</div>
                  <div>Account</div>
                  <div>Last sent</div>
                  <div className='text-right'>Actions</div>
                </div>
                {paginatedRecipients.map((recipient) => {
                  const countryInfo = getCountryInfo(recipient, configCurrencies);
                  const lastSentDate = getLastSentDate(recipient, lastSentMap);
                  const lastSentLabel = formatRelativeLastSent(lastSentDate);
                  const canSend = canSendToRecipient(recipient);

                  return (
                    <RecipientRow
                      key={recipient.id}
                      recipient={recipient}
                      countryInfo={countryInfo}
                      lastSentLabel={lastSentLabel}
                      canSend={canSend}
                      isHighlighted={String(recipient.id) === String(highlightedRecipientId)}
                      onSend={(id) => navigate(`/send-money/${encodeURIComponent(id)}`)}
                      onDelete={handleDeleteClick}
                      onOpen={(id) => navigate(`/recipients/${encodeURIComponent(id)}`)}
                    />
                  );
                })}
              </div>

              <div className='flex flex-wrap items-center justify-between gap-3 px-1'>
                <p className='text-xs tabular-nums text-[var(--text-tertiary)]'>
                  Showing <strong className='font-medium text-[var(--n800)]'>{startItem}–{endItem}</strong> of{' '}
                  <strong className='font-medium text-[var(--n800)]'>{totalItems}</strong> recipients
                </p>
                <div className='flex items-center gap-1'>
                  <button
                    type='button'
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className='inline-flex h-8 min-w-8 items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-2.5 text-[12.5px] font-medium text-[var(--n800)] disabled:cursor-not-allowed disabled:opacity-40'
                  >
                    <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                      <path d='M15 18l-6-6 6-6' />
                    </svg>
                    Prev
                  </button>
                  {pageNumbers.map((page) =>
                    page === '…' ? (
                      <span key='pagination-ellipsis' className='px-1 text-sm text-[var(--text-tertiary)]'>
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        type='button'
                        onClick={() => handlePageChange(page)}
                        className={`inline-flex h-8 min-w-8 items-center justify-center rounded-lg px-2.5 text-[12.5px] font-medium tabular-nums ${
                          currentPage === page
                            ? 'border border-[var(--n900)] bg-[var(--n900)] text-white'
                            : 'border border-[var(--color-border)] bg-[var(--white)] text-[var(--n800)]'
                        }`}
                      >
                        {page}
                      </button>
                    ),
                  )}
                  <button
                    type='button'
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className='inline-flex h-8 min-w-8 items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--white)] px-2.5 text-[12.5px] font-medium text-[var(--n800)] disabled:cursor-not-allowed disabled:opacity-40'
                  >
                    Next
                    <svg width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
                      <path d='M9 18l6-6-6-6' />
                    </svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <ErrorToast error={getRecipientsError} />
      <ErrorToast error={deleteRecipientError} />
      <SuccessToast message={showSuccessToast ? 'Recipient deleted successfully' : null} onDismiss={() => setShowSuccessToast(false)} />

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        recipientName={deleteModal.recipientName}
        isDeleting={deletingRecipient || false}
      />
    </Layout>
  );
}

export default Recipients;
